import GenericFormModal from "#blocks/Form/GenericFormModal"
import Firebase from "#classes/Firebase"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import {
  AddTagFormSubmitValues,
  AddTagFormValues,
} from "#customTypes/composedTypes/tagTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { useFirebaseTransaction } from "#hooks/reactQuery/useFirebaseMutation"

import AddTagForm from "./AddTagForm"

export interface AddTagFormModalProps {
  onClose: () => void
  initialValues: AddTagFormValues | undefined
}

const AddTagFormModal = ({ onClose, initialValues }: AddTagFormModalProps) => {
  const isEdit = initialValues?.id != null

  const handleFinish = async (
    cleanedValues: AddTagFormValues
  ): Promise<void> => {
    const data: AddTagFormValues | AddTagFormSubmitValues = cleanedValues

    if (initialValues?.id != null) {
      data.id = initialValues.id
    }
    console.log("✅🔥 submitted values => ", data)

    // TODO: Fix type error - can't submit undefined values
    await Firebase.getInstance().addDocument(DOC_TYPES.TAGS, data)
  }

  const { mutate: handleTransaction } = useFirebaseTransaction()

  const handleDelete = async () => {
    if (!initialValues?.id) return

    const tagValue = initialValues.id

    // Check if any students are using this tag
    const studentsPromise =
      Firebase.getInstance().variableQuery<StudentConstructor>(
        DOC_TYPES.STUDENTS,
        {
          queries: {
            tags: {
              value: tagValue,
              comparator: COMPARATORS.CONTAINS,
            },
          },
        }
      )

    // Check if any teachers are using this tag
    const teachersPromise =
      Firebase.getInstance().variableQuery<UserConstructor>(DOC_TYPES.USERS, {
        queries: {
          tags: {
            value: tagValue,
            comparator: COMPARATORS.CONTAINS,
          },
        },
      })

    const [students, teachers] = await Promise.all([
      studentsPromise,
      teachersPromise,
    ])

    const errors: string[] = []

    if (students.length > 0) {
      errors.push(
        `Please remove this tag from the following students before deleting it: 
      
${students.map((s) => s.name).join("\n")}`
      )
    }

    if (teachers.length > 0) {
      errors.push(
        `Please remove this tag from the following teachers before deleting it: 
      
${teachers.map((t) => t.name).join("\n")}`
      )
    }

    if (errors.length > 0) {
      throw new Error(errors.join("\n\n"))
    }

    await handleTransaction([
      {
        docType: DOC_TYPES.TAGS,
        id: initialValues.id,
        data: null,
        action: "delete",
      },
    ])

    // Brief delay to allow Firebase listeners to process the deletion
    setTimeout(() => {
      onClose()
    }, 500)
  }

  return (
    <GenericFormModal
      title={isEdit ? "Edit Tag" : "Add Tag"}
      onClose={onClose}
      initialValues={initialValues}
      onFinish={handleFinish}
      resource={RESOURCES.TAG}
      onDelete={handleDelete}
    >
      <AddTagForm />
    </GenericFormModal>
  )
}

export default AddTagFormModal
